import axios from 'axios';
import moment from 'moment-timezone';

import Mgr from '@/core/services/security.service';

const STATUS_CODE = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
  FORBIDDEN: 403,
  SERVER_ERROR: 500,
};

var user = new Mgr();

// Set config defaults when creating the instance
const instance = axios.create({
  baseURL: process.env.VUE_APP_ZALO_WEBHOOK_URL,
  withCredentials: false,
});

// Alter defaults after instance has been created
instance.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
instance.defaults.headers.common['Access-Control-Allow-Credentials'] = 'true';
instance.defaults.headers.common['Content-Type'] =
  'application/json; charset=utf-8';
instance.defaults.headers.common['Accept'] = 'application/json';
instance.defaults.headers.common['X-Timezone-Offset'] = moment.tz.guess();
instance.defaults.headers.common['Device'] = 'Web';

// Add a request interceptor
instance.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error),
);

// Add a response interceptor
instance.interceptors.response.use(
  (response) => {
    if (response.status === 200 || response.status === 201) {
      return Promise.resolve(response);
    } else {
      return Promise.reject(response);
    }
  },
  (error) => {
    if (error.response && error.response.status) {
      switch (error.response.status) {
        case STATUS_CODE.BAD_REQUEST:
          break;
        case STATUS_CODE.UNAUTHORIZED:
          break;
        case STATUS_CODE.FORBIDDEN:
          break;
        case STATUS_CODE.NOT_FOUND:
          break;
        case STATUS_CODE.SERVER_ERROR:
          break;
      }
      return Promise.reject(error.response);
    }
    return Promise.reject(error);
  },
);

/**
 * Service to call Webhook HTTP request via Axios
 */
export default class WebhookService {
  /**
   * Define header HTTP request
   */
  async defineHeaderAxios() {
    await user.getAcessToken().then((acessToken) => {
      instance.defaults.headers.common['Authorization'] =
        'Bearer ' + acessToken;
    });
  }

  /**
   * Send the GET HTTP request
   */
  async get(url, config = {}) {
    await this.defineHeaderAxios();
    try {
      let res = await instance.get(url, { ...config });
      if (res.status === 200 || res.status === 201) {
        if (config.responseType) {
          return res;
        }
        return res.data;
      }
    } catch (error) {
      if (error && error.data && error.status !== 401) {
        if (error.data.error) {
          throw error.data.error;
        }
        throw error.data;
      } else {
        if (error.status == 401) {
          await user.signOut();
          throw 'Phiên đăng nhập hết hạn';
        }
        throw 'Server không response';
      }
    }
  }

  /**
   * POST multipart/form-data (file upload)
   */
  async postForm(url, formData) {
    await this.defineHeaderAxios();
    try {
      const res = await instance.post(url, formData, {
        headers: { 'Content-Type': undefined },
      });
      if (res.status === 200 || res.status === 201) return res.data;
    } catch (error) {
      if (error?.data) throw error.data;
      if (error?.status === 401) { await user.signOut(); throw 'Phiên đăng nhập hết hạn'; }
      throw { code: 'unknow', message: 'Server không response' };
    }
  }

  /**
   * Set the POST HTTP request
   */
  async post(url, data, config = {}) {
    await this.defineHeaderAxios();
    try {
      let res = await instance.post(url, data, { ...config });
      if (res.status === 200 || res.status === 201) {
        if (config.responseType) {
          return res;
        }
        return res.data;
      }
    } catch (error) {
      if (error) {
        if (error.data) {
          if (error.data.error) {
            throw error.data.error;
          }
          throw error.data;
        } else if (error.status === 401) {
          await user.signOut();
          throw 'Phiên đăng nhập hết hạn';
        }
      }
      throw { code: 'unknow', message: 'Server không response' };
    }
  }

  /**
   * Send the PUT HTTP request
   */
  async put(url, data, config = {}) {
    await this.defineHeaderAxios();
    try {
      let res = await instance.put(url, data, { ...config });
      if (res.status === 200 || res.status === 201) {
        return res.data;
      }
    } catch (error) {
      if (error) {
        if (error.data) {
          if (error.data.error) {
            throw error.data.error;
          }
          throw error.data;
        } else if (error.status === 401) {
          await user.signOut();
          throw 'Phiên đăng nhập hết hạn';
        }
      }
      throw { code: 'unknow', message: 'Server không response' };
    }
  }

  /**
   * Send the DELETE HTTP request
   */
  async delete(url, config = {}) {
    await this.defineHeaderAxios();
    try {
      let res = await instance.delete(url, { ...config });
      if (res.status === 200 || res.status === 201) {
        return res.data;
      }
    } catch (error) {
      if (error && error.data) {
        if (error.data.error) {
          throw error.data.error;
        }
        throw error.data;
      }
      throw error;
    }
  }
}
